import { useState } from 'react';

import CareCircles from '@/cam-admin/care-circles/CareCircles';
import { type CareSearchInitial } from '@/cam-admin/care-search/CareSearch';
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import DescopingCaseloads from '@/cam-admin/descoping/DescopingCaseloads';
import {
  CASELOADS_PATH,
  CIRCLE_PATH,
  CIRCLES_PATH,
  DESCOPING_NAV,
} from '@/cam-admin/descoping/descopingNav';

type Section = 'circles' | 'caseloads' | 'circle';

/**
 * Descoping CAM July 2026 — the CAM admin, pared back for the July 2026 descoping
 * work. Three sections, switched from the nav rail (so it all stays on the single
 * /descoping-cam route and never leaves the prototype):
 *  - Your caseloads: the signed-in case manager's circles + activity log.
 *  - All care circles: the full circles index (Circles tab only).
 *  - A care circle's control panel, opened from either list.
 * Only this prototype is descoped; the live CAM screens are untouched.
 * Presentational — routing is injected via `onNavigate`.
 */
const Descoping = ({
  careSearch,
  onNavigate,
}: {
  // Pass-through state for the control panel's care search tab (the stage's
  // state select uses it for the care-secured-elsewhere state).
  careSearch?: CareSearchInitial;
  onNavigate?: (path: string) => void;
}) => {
  const [section, setSection] = useState<Section>('circles');

  // The descoping rail and the in-prototype links switch the on-screen section
  // rather than navigating; anything else falls through to the router.
  const handleNavigate = (path: string) => {
    if (path === CASELOADS_PATH) return setSection('caseloads');
    if (path === CIRCLES_PATH) return setSection('circles');
    if (path.startsWith(CIRCLE_PATH)) return setSection('circle');
    onNavigate?.(path);
  };

  if (section === 'circle') {
    // The shared control panel, shown with the descoping rail and a back target
    // inside the prototype — opening a circle keeps you in the descoping project.
    return (
      <ControlPanel
        embedded
        initialTab='overview'
        careSearch={careSearch}
        nav={DESCOPING_NAV}
        activeNav='All care circles'
        backPath={CIRCLES_PATH}
        tabLabels={{ calls: 'Call notes' }}
        descopedCallNotes
        onNavigate={handleNavigate}
      />
    );
  }

  if (section === 'caseloads') {
    return (
      <DescopingCaseloads nav={DESCOPING_NAV} onNavigate={handleNavigate} />
    );
  }

  return (
    <CareCircles
      tabs={['circles']}
      nav={DESCOPING_NAV}
      activeNav='All care circles'
      onNavigate={handleNavigate}
      onOpenCircle={() => handleNavigate(CIRCLE_PATH)}
    />
  );
};

export default Descoping;
